// hooks/useCartFullData.ts
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
    getDocs,
    orderBy,
    Timestamp,
    addDoc,
    updateDoc
} from 'firebase/firestore';

export interface CartPerson {
    id: string;
    displayName: string;
    email: string;
    phoneNumber: string;
    photoUrl?: string;
    userId: string;
    isAssignedCart: boolean;
    status: string;
    address?: string;
    canCollectCash?: boolean;
    cartState?: string;
    cartType?: string;
    deliveryCartsIds?: string[];
    isAnonymousUser?: boolean;
    isOnline?: boolean;
    supportedChannels?: string[];
    createdAt?: any;
    updatedAt?: any;
}

export interface SalesWalletTransaction {
    id: string;
    amount: number;
    createdAt: any;
    description?: string;
    mainTransactionId?: string;
    paymentMethod: string;
    transactionTag?: string;
    type: string;
    [key: string]: any;
}

export interface SalesWallet {
    id: string;
    cashSales: number;
    creditCardSales: number;
    currency: string;
    createdAt?: any;
    updatedAt?: any;
    userId: string;
}

export interface PurchaseWallet {
    id: string;
    balance: number;
    myEarnings: number;
    payableToPlatform: number;
    currency: string;
    createdAt?: Timestamp | any;
    updatedAt?: Timestamp | any;
    [key: string]: any;
}

export interface PurchaseWalletTransaction {
    id: string;
    amount: number;
    createdAt: any;
    description?: string;
    status?: string;
    transactionTag?: string;
    type: string;
    walletId?: string;
    restaurantId?: string;
    [key: string]: any;
}

export interface CartStatistics {
    id: string;
    allOrderCount: number;
    cancelledOrderCount: number;
    cardSales: number;
    cashSales: number;
    completedOrderCount: number;
    pendingOrderCount: number;
    date: string;
    createdAt?: Timestamp | any;
    updatedAt?: Timestamp | any;
}

export interface CartFullData {
    // Cart Person
    cartPerson: CartPerson | null;
    cartPersonId: string | null;
    
    // Sales Wallet
    salesWallet: SalesWallet | null;
    salesTransactions: SalesWalletTransaction[];
    salesDailySummary: {
        cashSales: number;
        cardSales: number;
        total: number;
        count: number;
    };
    
    // Purchase Wallet
    purchaseWallet: PurchaseWallet | null;
    purchaseTransactions: PurchaseWalletTransaction[];
    purchaseDailySummary: {
        totalAmount: number;
        count: number;
        payouts: number;
        settlements: number;
    };
    
    // Cart Statistics
    cartStatistics: CartStatistics | null;
    
    // Combined transactions for accordion (grouped by transactionTag)
    groupedTransactions: {
        [key: string]: {
            totalAmount: number;
            transactions: any[];
        };
    };
    
    // Loading states
    loading: boolean;
    loadingCartPerson: boolean;
    loadingSalesWallet: boolean;
    loadingPurchaseWallet: boolean;
    loadingStatistics: boolean;
    
    // Error states
    error: Error | null;
    errorCartPerson: Error | null;
    errorSalesWallet: Error | null;
    errorPurchaseWallet: Error | null;
    errorStatistics: Error | null;
    
    // Settlement function
    settlePayment: (restaurantId: string) => Promise<void>;
    isSettling: boolean;
}

interface UseCartFullDataProps {
    cartId: string;
    selectedDate?: string;
    skip?: boolean;
}

const formatDateToStatsId = (date: string): string => {
    return date.replace(/-/g, '');
};

const getDateKey = (createdAt: any, fallbackDate: string): string => {
    try {
        if (createdAt?.toDate) {
            return createdAt.toDate().toISOString().split('T')[0];
        } else if (createdAt) {
            return new Date(createdAt).toISOString().split('T')[0];
        }
    } catch (e) {
        console.warn('Date parsing error:', e);
    }
    return fallbackDate;
};

const transformSalesTransaction = (transaction: any, dateKey: string) => ({
    id: transaction.id || `sales-${Date.now()}`,
    transactionId: transaction.id,
    amount: Number(transaction.amount) || 0,
    paymentMethod: transaction.paymentMethod || 'Cash',
    type: transaction.type || 'Credit',
    description: transaction.description || 'Sale transaction',
    orderId: transaction.mainTransactionId || transaction.id,
    createdAt: transaction.createdAt || new Date(),
    cashAmount: transaction.paymentMethod?.toLowerCase() === 'cash' ? Number(transaction.amount) || 0 : 0,
    cardAmount: transaction.paymentMethod?.toLowerCase() === 'card' || transaction.paymentMethod?.toLowerCase() === 'credit card' ? Number(transaction.amount) || 0 : 0,
    totalAmount: Number(transaction.amount) || 0,
    transactionTag: transaction.transactionTag || 'Sales',
    dateKey
});

const transformPurchaseTransaction = (transaction: any, dateKey: string) => ({
    id: transaction.id || `purchase-${Date.now()}`,
    transactionId: transaction.id,
    amount: Number(transaction.amount) || 0,
    paymentMethod: 'Platform',
    type: transaction.type || 'Debit',
    description: transaction.description || 'Platform payout',
    orderId: transaction.id,
    createdAt: transaction.createdAt || new Date(),
    cashAmount: 0,
    cardAmount: 0,
    totalAmount: Number(transaction.amount) || 0,
    transactionTag: transaction.transactionTag || 'PlatformPayout',
    status: transaction.status || 'Completed',
    dateKey
});

export const useCartFullData = ({
    cartId,
    selectedDate,
    skip = false
}: UseCartFullDataProps): CartFullData => {
    
    // Cart Person
    const [cartPerson, setCartPerson] = useState<CartPerson | null>(null);
    const [cartPersonId, setCartPersonId] = useState<string | null>(null);
    const [loadingCartPerson, setLoadingCartPerson] = useState(true);
    const [errorCartPerson, setErrorCartPerson] = useState<Error | null>(null);
    
    // Sales Wallet
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [salesWallet, setSalesWallet] = useState<SalesWallet | null>(null);
    const [salesTransactions, setSalesTransactions] = useState<SalesWalletTransaction[]>([]);
    const [salesDailySummary, setSalesDailySummary] = useState({
        cashSales: 0,
        cardSales: 0,
        total: 0,
        count: 0
    });
    const [loadingSalesWallet, setLoadingSalesWallet] = useState(true);
    const [errorSalesWallet, setErrorSalesWallet] = useState<Error | null>(null);
    
    // Purchase Wallet
    const [purchaseWallet, setPurchaseWallet] = useState<PurchaseWallet | null>(null);
    const [purchaseTransactions, setPurchaseTransactions] = useState<PurchaseWalletTransaction[]>([]);
    const [purchaseDailySummary, setPurchaseDailySummary] = useState({
        totalAmount: 0,
        count: 0,
        payouts: 0,
        settlements: 0
    });
    const [loadingPurchaseWallet, setLoadingPurchaseWallet] = useState(true);
    const [errorPurchaseWallet, setErrorPurchaseWallet] = useState<Error | null>(null);
    const [isSettling, setIsSettling] = useState(false);
    
    // Cart Statistics
    const [cartStatistics, setCartStatistics] = useState<CartStatistics | null>(null);
    const [loadingStatistics, setLoadingStatistics] = useState(true);
    const [errorStatistics, setErrorStatistics] = useState<Error | null>(null);
    
    // Combined transactions
    const [groupedTransactions, setGroupedTransactions] = useState({});
    
    // Overall loading and error
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // ============ EFFECT 1: FETCH CART PERSON BY CART ID ============
    useEffect(() => {
        if (!cartId || skip) {
            setLoadingCartPerson(false);
            setCartPerson(null);
            setCartPersonId(null);
            return;
        }

        setLoadingCartPerson(true);
        setErrorCartPerson(null);

        const fetchCartPerson = async () => {
            try {
                // Get cart to find userId
                const cartDocRef = doc(db, "Carts", cartId);
                const cartDoc = await getDoc(cartDocRef);

                if (!cartDoc.exists()) {
                    setCartPerson(null);
                    setCartPersonId(null);
                    setLoadingCartPerson(false);
                    return;
                }

                const cartData = cartDoc.data();
                const userId = cartData?.owner?.userId || cartData?.userId;

                if (!userId) {
                    setCartPerson(null);
                    setCartPersonId(null);
                    setLoadingCartPerson(false);
                    return;
                }

                // Find cart person by userId
                const cartPersonsRef = collection(db, "CartPersons");
                const q = query(cartPersonsRef, where("userId", "==", userId));

                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const doc = querySnapshot.docs[0];
                    setCartPersonId(doc.id);
                    setCartPerson({
                        id: doc.id,
                        ...doc.data()
                    } as CartPerson);
                } else {
                    setCartPerson(null);
                    setCartPersonId(null);
                }
                setLoadingCartPerson(false);
                setErrorCartPerson(null);
            } catch (err) {
                console.error("❌ Error fetching cart person:", err);
                setErrorCartPerson(err as Error);
                setLoadingCartPerson(false);
            }
        };

        fetchCartPerson();
    }, [cartId, skip]);

    // ============ EFFECT 2: FETCH SALES WALLET TRANSACTIONS ============
    useEffect(() => {
        if (!cartPersonId || skip) {
            setLoadingSalesWallet(false);
            setSalesTransactions([]);
            setSalesDailySummary({ cashSales: 0, cardSales: 0, total: 0, count: 0 });
            return;
        }

        const dateToUse = selectedDate || new Date().toISOString().split('T')[0];
        
        setLoadingSalesWallet(true);
        setErrorSalesWallet(null);

        // Parse selected date
        const selectedDateObj = new Date(dateToUse);
        selectedDateObj.setHours(0, 0, 0, 0);
        
        const nextDay = new Date(selectedDateObj);
        nextDay.setDate(nextDay.getDate() + 1);

        // Convert to Firestore timestamps
        const startTimestamp = Timestamp.fromDate(selectedDateObj);
        const endTimestamp = Timestamp.fromDate(nextDay);

        // Reference to transactions
        const transactionsRef = collection(
            db, 
            'CartPersons', 
            cartPersonId, 
            'wallets', 
            'salesWallet', 
            'transactions'
        );
        
        const q = query(
            transactionsRef,
            where('createdAt', '>=', startTimestamp),
            where('createdAt', '<', endTimestamp),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const transactionList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as SalesWalletTransaction[];

                setSalesTransactions(transactionList);

                // Calculate summary
                const summary = transactionList.reduce(
                    (acc, t) => {
                        const amount = t.amount || 0;
                        if (t.paymentMethod?.toLowerCase() === 'cash') {
                            acc.cashSales += amount;
                        }
                        if (t.paymentMethod?.toLowerCase() === 'card' || 
                            t.paymentMethod?.toLowerCase() === 'credit card') {
                            acc.cardSales += amount;
                        }
                        acc.total += amount;
                        acc.count += 1;
                        return acc;
                    },
                    { cashSales: 0, cardSales: 0, total: 0, count: 0 }
                );

                setSalesDailySummary(summary);
                setLoadingSalesWallet(false);
                setErrorSalesWallet(null);
            },
            (err) => {
                console.error("❌ Error fetching sales wallet transactions:", err);
                setErrorSalesWallet(err as Error);
                setLoadingSalesWallet(false);
            }
        );

        return () => unsubscribe();
    }, [cartPersonId, selectedDate, skip]);

    // ============ EFFECT 3: FETCH PURCHASE WALLET ============
    useEffect(() => {
        if (!cartPersonId || skip) {
            setLoadingPurchaseWallet(false);
            setPurchaseWallet(null);
            setPurchaseTransactions([]);
            return;
        }

        setLoadingPurchaseWallet(true);
        setErrorPurchaseWallet(null);

        const walletDocRef = doc(
            db,
            "CartPersons",
            cartPersonId,
            "wallets",
            "purchaseWallet",
        );

        const unsubscribeWallet = onSnapshot(
            walletDocRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    setPurchaseWallet({
                        id: docSnap.id,
                        ...docSnap.data(),
                    } as PurchaseWallet);

                    // Fetch transactions subcollection
                    const transactionsRef = collection(
                        db,
                        "CartPersons",
                        cartPersonId,
                        "wallets",
                        "purchaseWallet",
                        "transactions"
                    );
                    
                    const transactionsQuery = query(
                        transactionsRef,
                        orderBy('createdAt', 'desc')
                    );

                    const unsubscribeTransactions = onSnapshot(
                        transactionsQuery,
                        (transactionsSnapshot) => {
                            const transactionsList = transactionsSnapshot.docs.map(doc => ({
                                id: doc.id,
                                ...doc.data()
                            })) as PurchaseWalletTransaction[];

                            setPurchaseTransactions(transactionsList);
                            setLoadingPurchaseWallet(false);
                        },
                        (err) => {
                            console.error("❌ Error fetching purchase wallet transactions:", err);
                            setErrorPurchaseWallet(err as Error);
                            setLoadingPurchaseWallet(false);
                        }
                    );

                    return () => unsubscribeTransactions();
                } else {
                    setPurchaseWallet(null);
                    setPurchaseTransactions([]);
                    setLoadingPurchaseWallet(false);
                    setErrorPurchaseWallet(null);
                }
            },
            (err) => {
                console.error("❌ Error fetching purchase wallet:", err);
                setErrorPurchaseWallet(err as Error);
                setLoadingPurchaseWallet(false);
            },
        );

        return () => unsubscribeWallet();
    }, [cartPersonId, skip]);

    // ============ EFFECT 4: FETCH PURCHASE WALLET TRANSACTIONS BY DATE ============
    useEffect(() => {
        if (!cartPersonId || skip) {
            setPurchaseDailySummary({ totalAmount: 0, count: 0, payouts: 0, settlements: 0 });
            return;
        }

        const dateToUse = selectedDate || new Date().toISOString().split('T')[0];
        
        const selectedDateObj = new Date(dateToUse);
        selectedDateObj.setHours(0, 0, 0, 0);
        
        const nextDay = new Date(selectedDateObj);
        nextDay.setDate(nextDay.getDate() + 1);

        const startTimestamp = Timestamp.fromDate(selectedDateObj);
        const endTimestamp = Timestamp.fromDate(nextDay);

        const transactionsRef = collection(
            db,
            "CartPersons",
            cartPersonId,
            "wallets",
            "purchaseWallet",
            "transactions"
        );
        
        const q = query(
            transactionsRef,
            where('createdAt', '>=', startTimestamp),
            where('createdAt', '<', endTimestamp),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const transactionList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as PurchaseWalletTransaction[];

                const summary = transactionList.reduce(
                    (acc, t) => {
                        const amount = t.amount || 0;
                        acc.totalAmount += amount;
                        acc.count += 1;
                        
                        if (t.transactionTag === 'PlatformPayout' || t.type === 'Debit') {
                            acc.payouts += amount;
                        }
                        if (t.transactionTag === 'settlement' || t.description?.includes('settlement')) {
                            acc.settlements += amount;
                        }
                        
                        return acc;
                    },
                    { totalAmount: 0, count: 0, payouts: 0, settlements: 0 }
                );

                setPurchaseDailySummary(summary);
            },
            (err) => {
                console.error("❌ Error fetching purchase wallet transactions by date:", err);
            }
        );

        return () => unsubscribe();
    }, [cartPersonId, selectedDate, skip]);

    // ============ EFFECT 5: FETCH CART STATISTICS ============
    useEffect(() => {
        if (!cartId || skip) {
            setLoadingStatistics(false);
            setCartStatistics(null);
            return;
        }

        setLoadingStatistics(true);
        setErrorStatistics(null);

        const dateToUse = selectedDate || new Date().toISOString().split('T')[0];
        const formattedDate = formatDateToStatsId(dateToUse);
        
        const statisticsDocRef = doc(
            db,
            "Carts",
            cartId,
            "Statistics",
            formattedDate
        );

        const unsubscribe = onSnapshot(
            statisticsDocRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    setCartStatistics({
                        id: docSnap.id,
                        ...docSnap.data(),
                    } as CartStatistics);
                } else {
                    // Return default statistics if no document exists
                    setCartStatistics({
                        id: formattedDate,
                        allOrderCount: 0,
                        cancelledOrderCount: 0,
                        cardSales: 0,
                        cashSales: 0,
                        completedOrderCount: 0,
                        pendingOrderCount: 0,
                        date: formattedDate
                    });
                }
                setLoadingStatistics(false);
                setErrorStatistics(null);
            },
            (err) => {
                console.error("❌ Error fetching cart statistics:", err);
                setErrorStatistics(err as Error);
                setLoadingStatistics(false);
            }
        );

        return () => unsubscribe();
    }, [cartId, selectedDate, skip]);

    // ============ EFFECT 6: COMBINE TRANSACTIONS FOR ACCORDION ============
    useEffect(() => {
        const dateToUse = selectedDate || new Date().toISOString().split('T')[0];
        const groupedByTag: any = {};

        // Add Sales Wallet Transactions
        if (salesTransactions && Array.isArray(salesTransactions)) {
            salesTransactions.forEach(transaction => {
                if (!transaction) return;
                
                const tag = transaction.transactionTag || 'Sales';
                const amount = Number(transaction.amount) || 0;
                const dateKey = getDateKey(transaction.createdAt, dateToUse);
                
                if (!groupedByTag[tag]) {
                    groupedByTag[tag] = {
                        totalAmount: 0,
                        transactions: []
                    };
                }
                
                groupedByTag[tag].totalAmount += amount;
                groupedByTag[tag].transactions.push(transformSalesTransaction(transaction, dateKey));
            });
        }

        // Add Purchase Wallet Transactions
        if (purchaseTransactions && Array.isArray(purchaseTransactions)) {
            // Filter by selected date
            const selectedDateObj = new Date(dateToUse);
            selectedDateObj.setHours(0, 0, 0, 0);
            const nextDay = new Date(selectedDateObj);
            nextDay.setDate(nextDay.getDate() + 1);
            
            const filteredPurchaseTransactions = purchaseTransactions.filter(t => {
                const transactionDate = t.createdAt?.toDate?.() || new Date(t.createdAt);
                return transactionDate >= selectedDateObj && transactionDate < nextDay;
            });

            filteredPurchaseTransactions.forEach(transaction => {
                if (!transaction) return;
                
                const tag = transaction.transactionTag || 'PlatformPayout';
                const amount = Number(transaction.amount) || 0;
                const dateKey = getDateKey(transaction.createdAt, dateToUse);
                
                if (!groupedByTag[tag]) {
                    groupedByTag[tag] = {
                        totalAmount: 0,
                        transactions: []
                    };
                }
                
                groupedByTag[tag].totalAmount += amount;
                groupedByTag[tag].transactions.push(transformPurchaseTransaction(transaction, dateKey));
            });
        }

        // Sort transactions within each group by date (newest first)
        Object.keys(groupedByTag).forEach(tag => {
            if (groupedByTag[tag].transactions && Array.isArray(groupedByTag[tag].transactions)) {
                groupedByTag[tag].transactions.sort((a: any, b: any) => {
                    try {
                        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                        return dateB.getTime() - dateA.getTime();
                    } catch {
                        return 0;
                    }
                });
            }
        });

        setGroupedTransactions(groupedByTag);

    }, [salesTransactions, purchaseTransactions, selectedDate]);

    // ============ EFFECT 7: OVERALL LOADING STATE ============
    useEffect(() => {
        const isLoading = loadingCartPerson || loadingSalesWallet || loadingPurchaseWallet || loadingStatistics;
        setLoading(isLoading);
    }, [loadingCartPerson, loadingSalesWallet, loadingPurchaseWallet, loadingStatistics]);

    // ============ EFFECT 8: OVERALL ERROR STATE ============
    useEffect(() => {
        const err = errorCartPerson || errorSalesWallet || errorPurchaseWallet || errorStatistics;
        setError(err);
    }, [errorCartPerson, errorSalesWallet, errorPurchaseWallet, errorStatistics]);

    // ============ SETTLE PAYMENT FUNCTION ============
    const settlePayment = async (restaurantId: string) => {
        if (!cartPersonId) {
            throw new Error("No cart person ID found");
        }

        setIsSettling(true);
        try {
            const walletRef = doc(
                db,
                "CartPersons",
                cartPersonId,
                "wallets",
                "purchaseWallet",
            );

            const walletDoc = await getDoc(walletRef);
            if (!walletDoc.exists()) {
                throw new Error("Purchase wallet not found");
            }

            const currentData = walletDoc.data();
            const amount = currentData.payableToPlatform || 0;

            await updateDoc(walletRef, {
                payableToPlatform: 0,
                balance: (currentData.balance || 0) + amount,
                updatedAt: Timestamp.now(),
            });

            const transactionsRef = collection(
                db,
                "CartPersons",
                cartPersonId,
                "wallets",
                "purchaseWallet",
                "transactions",
            );
            
            await addDoc(transactionsRef, {
                amount: amount,
                restaurantId,
                createdAt: Timestamp.now(),
                status: "Completed",
                type: "Debit",
                transactionTag: "PlatformPayout",
                description: "Payout platform fee to restaurant",
                walletId: "purchaseWallet"
            });
        } catch (err) {
            console.error("❌ Error settling payment:", err);
            throw err;
        } finally {
            setIsSettling(false);
        }
    };

    // ============ RETURN ============
    return {
        // Cart Person
        cartPerson,
        cartPersonId,
        
        // Sales Wallet
        salesWallet,
        salesTransactions,
        salesDailySummary,
        
        // Purchase Wallet
        purchaseWallet,
        purchaseTransactions,
        purchaseDailySummary,
        
        // Cart Statistics
        cartStatistics,
        
        // Combined transactions
        groupedTransactions,
        
        // Loading states
        loading,
        loadingCartPerson,
        loadingSalesWallet,
        loadingPurchaseWallet,
        loadingStatistics,
        
        // Error states
        error,
        errorCartPerson,
        errorSalesWallet,
        errorPurchaseWallet,
        errorStatistics,
        
        // Settlement
        settlePayment,
        isSettling
    };
};